export type MetricMode = 'count' | 'pct'

type Props = {
  value: MetricMode
  onChange: (mode: MetricMode) => void
  className?: string
  /** Accessible name for the control group. */
  label?: string
}

const btnBase =
  'px-2.5 py-1 text-xs font-semibold rounded-[var(--radius-sm)] border transition-colors'

const COUNT_TIP =
  'Count: unique suppliers by highest overall score (category/region/location). By brand uses supplier–brand lines.'

const PCT_TIP =
  'Percent: share of the filtered set (or portfolio when no filters). Band KPIs are exclusive and sum to 100%. Rollup High % is within that group.'

/** Shared Count | % control for Overview rollups and Intelligence KPIs. */
export function MetricModeToggle({
  value,
  onChange,
  className = '',
  label = 'Show counts or percentages',
}: Props) {
  return (
    <div
      className={`inline-flex items-center gap-0.5 p-0.5 rounded-[var(--radius-sm)] border border-es-hairline bg-es-haze/80 ${className}`}
      role="group"
      aria-label={label}
    >
      <button
        type="button"
        aria-pressed={value === 'count'}
        title={COUNT_TIP}
        className={`${btnBase} ${
          value === 'count'
            ? 'bg-es-white border-es-signal text-es-navy shadow-sm'
            : 'border-transparent text-es-muted hover:text-es-body'
        }`}
        onClick={() => onChange('count')}
      >
        Count
      </button>
      <button
        type="button"
        aria-pressed={value === 'pct'}
        title={PCT_TIP}
        className={`${btnBase} ${
          value === 'pct'
            ? 'bg-es-white border-es-signal text-es-navy shadow-sm'
            : 'border-transparent text-es-muted hover:text-es-body'
        }`}
        onClick={() => onChange('pct')}
      >
        %
      </button>
    </div>
  )
}

export function formatMetric(n: number, total: number, mode: MetricMode): string {
  if (mode === 'count') return String(n)
  if (total <= 0) return '0%'
  return `${Math.round((n / total) * 100)}%`
}

/**
 * Largest-remainder percentages so mutually exclusive parts sum to 100%.
 * Returns display strings like "42%" in the same order as `parts`.
 */
export function formatExclusivePercents(parts: number[]): string[] {
  const total = parts.reduce((sum, n) => sum + n, 0)
  if (total <= 0) return parts.map(() => '0%')
  const raw = parts.map((n) => (n / total) * 100)
  const floors = raw.map((x) => Math.floor(x + 1e-9))
  let remainder = 100 - floors.reduce((sum, n) => sum + n, 0)
  const order = raw
    .map((x, i) => ({ i, frac: x - floors[i] }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i)
  const out = [...floors]
  for (let k = 0; k < remainder; k++) {
    out[order[k % order.length].i] += 1
  }
  return out.map((n) => `${n}%`)
}
